import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import OurValues from "../section/OurValues";
import JoinOurTeam from "../section/JoinOurTeam";

export default function OurTeam() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get("https://ma-laravel.ma-uae.com/api/employees");
        setEmployees(response.data.data); // Access data inside "data"
        setLoading(false);
      } catch (err) {
        setError(t("Failed to load team data"));
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [t]);

  const ceo = employees.find((emp) => emp.type === "ceo");
  const regularEmployees = employees.filter((emp) => emp.type === "employee");

  return (
    <>
      {/* Hero Section */}
      <section className="our-team-landing relative bg-[#3896BE]/10 py-16">
        <div className="container h-full relative z-10 text-white flex flex-col items-center justify-center gap-[6px]">
          <div className="text-[50px] font-bold text-white">{t("Our Team")}</div>
          <div className="text-[20px] w-full md:w-1/2 text-center text-white">
            {t(
              "Meet the dedicated professionals who make MA Partners a leader in chartered accounting and business advisory services"
            )}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="relative py-[40px]">
        <div className="container flex flex-col gap-[30px]">
          <div className="flex flex-col items-center justify-center gap-[8px] text-center mb-12">
            <h2 className="text-[35px] font-bold">{t("Leadership Team")}</h2>
            <p className="text-lg text-[#404040] mx-auto max-w-[800px]">
              {t(
                "Our experienced leadership team combines deep industry knowledge with innovative thinking to guide clients toward financial success."
              )}
            </p>
          </div>

          {loading ? (
            <div className="text-center text-gray-600">{t("Loading...")}</div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : (
            <>
              {/* CEO Section */}
              {ceo ? (
                <div className="mb-12">
                  <div className="bg-white rounded-lg p-8 shadow-lg border border-[#3896BE]/20 hover:border-[#3896BE] transition-all duration-300">
                    <div className="flex flex-col items-center text-center">
                      <img
                        src={`https://ma-laravel.ma-uae.com/storage/app/public/${ceo.image_path}`}
                        alt={ceo.name}
                        className="w-32 h-32 rounded-full object-cover mb-4"
                      />
                      <div className="text-2xl font-semibold text-[#3896BE]">{ceo.name}</div>
                      <div className="text-lg text-gray-600 mb-3">{t(ceo.position)}</div>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {ceo.brief ? ceo.brief : t("")}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-600">{t("No CEO found")}</div>
              )}

              {/* Regular Employees Section */}
              {regularEmployees.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularEmployees.map((emp) => (
                    <div
                      key={emp.id}
                      className="bg-white rounded-lg p-6 shadow-lg border border-[#3896BE]/20 hover:border-[#3896BE] hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <img
                          src={`https://ma-laravel.ma-uae.com/storage/app/public/${emp.image_path}`}
                          alt={emp.name}
                          className="w-24 h-24 rounded-full object-cover mb-4"
                        />
                        <div className="text-xl font-semibold text-[#3896BE]">{emp.name}</div>
                        <div className="text-sm text-gray-600 mb-3">{t(emp.position)}</div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {emp.brief ? emp.brief : t("")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-600">{t("No team members found")}</div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Our Values Section */}
      <section className="relative py-[40px]">
        <div className="container flex flex-col gap-[30px]">
          <div className="flex flex-col items-center justify-center gap-[8px] text-center">
            <h2 className="text-[35px] font-bold">{t("Our Values")}</h2>
            <p className="text-lg text-[#404040] mx-auto max-w-[800px]">
              {t("The principles that guide our work and define our culture")}
            </p>
          </div>
          <div>
            <OurValues />
          </div>
        </div>
      </section>

      {/* Join Our Team Section */}
      <JoinOurTeam />
    </>
  );
}